import { useTranslation } from "react-i18next";
import { FileText, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PdfProfileRow } from "@/hooks/useOpenInspectionsQuery";

interface PdfProfilesSectionProps {
  profiles: PdfProfileRow[];
}

/**
 * PDF-Vorlagen subsection (TES-506 §4). Lists the available `pdf_profiles`
 * grouped by `protocol_type`, marking the default (`is_default`).
 *
 * GAP 2: There is NO Freigabe/approval column or per-inspection generated-PDF
 * table — `pdf_profiles` is config only. No approval workflow is rendered here.
 */
export function PdfProfilesSection({ profiles }: PdfProfilesSectionProps) {
  const { t } = useTranslation();

  // Group by protocol_type so the templates read by document family.
  const grouped = profiles.reduce<Record<string, PdfProfileRow[]>>((acc, profile) => {
    (acc[profile.protocol_type] ??= []).push(profile);
    return acc;
  }, {});
  const types = Object.keys(grouped).sort();

  return (
    <Card data-testid="pdf-profiles-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5 text-primary" />
          {t("openInspections.pdf.title")}
        </CardTitle>
        <CardDescription>{t("openInspections.pdf.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        {profiles.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="pdf-profiles-empty">
            {t("openInspections.pdf.empty")}
          </p>
        ) : (
          <div className="space-y-5">
            {types.map((type) => (
              <div key={type} data-testid={`pdf-profiles-group-${type}`}>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                  {t(`openInspections.pdf.protocolType.${type}`)}
                </h3>
                <ul className="space-y-2">
                  {grouped[type].map((profile) => (
                    <li
                      key={profile.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                      data-testid={`pdf-profile-${profile.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{profile.name}</p>
                        {profile.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {profile.description}
                          </p>
                        )}
                      </div>
                      {profile.is_default && (
                        <Badge variant="secondary" className="shrink-0 gap-1">
                          <Star className="h-3 w-3" />
                          {t("openInspections.pdf.default")}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
